import { User } from 'types/User';
import { APICore } from './apiCore';
import config from '../../config';
import { useApiCallsInterceptors } from './useApiCallsInterceptors';
import { Cotizaciones_RequestConsulta } from 'types/requests/Cotizaciones_RequestConsulta';
import { ClientAccounts_RequestConsulta } from 'types/requests/ClientAccounts_RequestConsulta';
import { ProcessExecution_GetRequest } from 'types/requests/ProcessExecution_GetRequest';
import moment from 'moment';
import { appVersionNumber } from '../../appConstants/einv_config_constants';
import { ImpersonateRequestDto } from 'types/Impersonate';

export function useApiCalls(url = config.REACT_APP_API_URL) {
    const interceptors = useApiCallsInterceptors();
    const api = new APICore();

    return {
        active: () => {
            return api.getLoggedInUser();
        },
        AdminDashboard: {
            getRdConnectionStatus: () => {
                return api.get(`${url}/admin/dashboard/rdconnection-status`, {});
            },
            getDataImpactStatus: () => {
                return api.get(`${url}/admin/dashboard/data-impact-status`, {});
            },
            getPaymentsStatus: (date?: Date) => {
                const params: any = {};
                if (date) {
                    params.date = moment(date).format('YYYY-MM-DD');
                }
                return api.get(`${url}/admin/dashboard/payments-status`, params);
            },
            getNotificationsStatus: (date?: Date) => {
                const params: any = {};
                if (date) {
                    params.date = moment(date).format('YYYY-MM-DD');
                }
                return api.get(`${url}/admin/dashboard/notifications-status`, params);
            },
            getClientsMetrics: () => {
                return api.get(`${url}/admin/dashboard/clients-metrics`, {});
            },
            getWitnessInstruments: () => {
                return api.get(`${url}/admin/dashboard/witness-instruments`, {});
            },
            addWitnessInstrument: (symbol: string) => {
                return api.post(`${url}/admin/dashboard/witness-instruments`, { symbol });
            },
            deleteWitnessInstrument: (id: number) => {
                return api.delete(`${url}/admin/dashboard/witness-instruments/${id}`);
            },
            validateWitnessInstruments: () => {
                return api.post(`${url}/admin/dashboard/witness-instruments/validate`, {});
            },
            fixSettlementDate: () => {
                return api.post(`${url}/admin/dashboard/fix-settlement-date`, {});
            },
            fixHoldingSummary: (request: { clientId: number; portfolioId: number; instrumentId: number | null; apply: boolean }) => {
                return api.post(`${url}/admin/dashboard/fix-holding-summary`, request);
            },
            fixExchangeRate: (request: { clientId: number; apply: boolean }) => {
                return api.post(`${url}/admin/dashboard/fix-exchange-rate`, request);
            },
            impersonate: (data: ImpersonateRequestDto) => api.post(`${url}/admin/dashboard/impersonate`, data),
        },
        Cashflow: {
            get: (id: any) => {
                return api.get(`${url}/cashflow/get`, { id: id });
            },
        },
        ClientAccount: {
            get: (filters: ClientAccounts_RequestConsulta) => {
                return api.get(`${url}/clientAccounts`, filters).then((response: any) => {
                    //TODO: los filtros de backend no están implementados. Implementación en front por ahora
                    if (response?.data?.length) {
                        response.data = response.data.filter((clientAccount: any) => {
                            // agentId:"1"
                            // agentTypeId:"2"
                            // currencyTypeId:"3950"
                            if (filters.currencyTypeId && clientAccount?.currency?.id != filters.currencyTypeId) {
                                return false;
                            }
                            if (filters.agentTypeId && clientAccount?.agent?.agentType?.id != filters.agentTypeId) {
                                return false;
                            }
                            if (filters.agentId && clientAccount?.agent?.id != filters.agentId) {
                                return false;
                            }
                            return true;
                        });
                    }
                    return response;
                });
            },
            insert: (data: any) => {
                return api.post(`${url}/clientAccount/insert`, data);
            },
            delete: (id: any) => {
                return api.post(`${url}/clientAccount/delete`, { id: id, isAvailable: false });
            },
            enable: (id: any) => {
                return api.post(`${url}/clientAccount/delete`, { id: id, isAvailable: true });
            },
        },
        Client: {
            get: () => {
                return api.get(`${url}/clients`, {});
            },
            getClient: () => {
                return api.get(`${url}/client`, {});
            },
            insert: (data: any) => {
                return api.post(`${url}/client/insert`, data);
            },
            update: (data: any) => {
                return api.post(`${url}/client/update`, data);
            },
            remove: (data: any) => {
                return api.post(`${url}/client/delete`, data);
            },
            composicionCliente: (filters: any) => {
                let listOfIntegerArrays = ['CurrencyIdList'];
                let queryString = getAsQueryString(filters, listOfIntegerArrays);

                return api.get(`${url}/totalValue/client-percentage?${queryString}`, null);
            },
        },
        CommercialAgent: {
            get: () => {
                return api.get(`${url}/commercialAgent`, {});
            },
            insert: (data: any) => {
                return api.post(`${url}/commercialAgent/insert`, data);
            },
            update: (data: any) => {
                return api.post(`${url}/commercialAgent/update`, data);
            },
            remove: (data: any) => {
                return api.post(`${url}/commercialAgent/delete`, data);
            },
        },
        Connections: {},
        Groups: {
            get: () => {
                return api.get(`${url}/groups`, {});
            },
            insert: (data: any) => {
                return api.create(`${url}/group/insert`, data);
            },
            update: (data: any) => {
                return api.post(`${url}/group/update`, data);
            },
            delete: (id: number) => {
                return api.post(`${url}/group/delete`, { id: id, isAvailable: false });
            },
            evolucion: (data: any) => {
                //TODO: el fake backend no intercepta llamadas get, por eso se usa un create. cambiar por get
                return api.create(`${url}/group/evolucion`, data);
            },
            composicion: (data: any) => {
                //TODO: el fake backend no intercepta llamadas get, por eso se usa un create. cambiar por get
                return api.create(`${url}/group/composicion`, data);
            },
            indicadores: (data: any) => {
                //TODO: el fake backend no intercepta llamadas get, por eso se usa un create. cambiar por get
                return api.create(`${url}/portfolio/indicadores`, data);
            },
        },
        ImpactoManual: {
            obtenerClientesConPosicion: (instrumentId: any, fecha: any) => {
                return api.get(`${url}/impacto-manual/obtener-clientes-con-posicion`, { instrumentId, fecha });
            },
            impactarPagosCuponAmortizacion: (data: any) => {
                return api.post(`${url}/impacto-manual/process-payments`, data);
            },
            ImpactarPagoDividendos: (data: any) => {
                return api.post(`${url}/impacto-manual/process-dividend-payments`, data);
            },
        },
        Instrumentos: {
            getInstruments: (instrumentType: number = 9) => {
                return api.get(`${url}/instruments`, {
                    instrumentType: instrumentType,
                    takeElement: 300,
                    isAvailable: true,
                });
            },
            getInstrumentsOData: (odataFilters: any) => {
                let promise = api.get(`${url}/instruments/odata`, odataFilters);
                interceptors.Instrumentos.ModifyODataInstruments(promise);
                return promise;
            },
        },
        /** Instrumentos custom por cliente (Admin con clientId, AccountHolder desde JWT) */
        CustomInstruments: {
            getByClient: (clientId: number) => {
                return api.get(`${url}/instruments/client`, { clientId });
            },
            create: (clientId: number, data: any) => {
                return api.post(`${url}/instruments/client?clientId=${clientId}`, data);
            },
            updatePrice: (instrumentId: number, clientId: number, data: any) => {
                return api.update(`${url}/instruments/client/${instrumentId}/price?clientId=${clientId}`, data);
            },
            delete: (instrumentId: number, clientId: number) => {
                return api.delete(`${url}/instruments/client/${instrumentId}?clientId=${clientId}`);
            },
        },
        /** Agentes custom por cliente (Admin con clientId, AccountHolder desde JWT) */
        CustomAgents: {
            getByClient: (clientId: number) => {
                return api.get(`${url}/params/agents/client`, { clientId });
            },
            createCustom: (clientId: number, data: any) => {
                return api.post(`${url}/params/agents/custom?clientId=${clientId}`, data);
            },
            deleteCustom: (agentId: number, clientId: number) => {
                return api.delete(`${url}/params/agents/custom/${agentId}?clientId=${clientId}`);
            },
        },
        Movimientos: {
            get: (odataFilters?: any) => {
                let promise = api.get(`${url}/transaction/get`, odataFilters);
                //TODO: en la consulta de movimiento se tienu modificar la cantidad a su valor absoluto porque en la inserción back inserta en negativo para débito
                interceptors.Movimientos.ModifyQuantity(promise);
                return promise;
            },
            getManualPaymentsToCancel: () => {
                let promise = api.get(`${url}/transaction/manual-payments-to-cancel`, {});
                interceptors.Movimientos.ModifyQuantity(promise);
                return promise;
            },
            getAgentAccounts: () => {
                let promise = api.get(`${url}/transaction/agent-account`, {});
                interceptors.Movimientos.MapAgentsAccounts(promise);
                return promise;
            },
            insertBatch: (data: any) => {
                return api.post(`${url}/insert-batch`, data);
            },
            delete: (data: any) => {
                return api.post(`${url}/delete`, data);
            },
            deleteTransaction: (movementId: number) => {
                return api.post(`${url}/transaction/delete`, { movementId });
            },
            update: (data: any) => {
                return api.post(`${url}/update-numero-operacion`, data);
            },
            insertTransaction: (data: any) => {
                return api.post(`${url}/insert-transaction`, data);
            },
            insertTransactions: (data: any) => {
                return api.post(`${url}/insert-transactions`, data);
            },
        },
        Organizations: {},
        Params: {
            get: () => {
                return api.get(`${url}/params`, {});
            },
            getTaxPayerTypes: () => {
                return api.get(`${url}/params/taxPayerTypes`, {});
            },
            getCountries: () => {
                return api.get(`${url}/params/countries`, {});
            },
            getConcepts: () => {
                return api.get(`${url}/params/concepts`, {});
            },
            getCurrencyTypes: () => {
                return api.get(`${url}/params/currencyTypes`, {});
            },
            getLanguages: () => {
                return api.get(`${url}/params/languages`, {});
            },
            getRoles: () => {
                return api.get(`${url}/params/userRoles`, {});
            },
            getTransactionTypes: () => {
                return api.get(`${url}/params/transactionTypes`, {});
            },
            getTransactionSubTypes: (transactionTypeId: any) => {
                let data = {
                    transactionTypeId: transactionTypeId,
                    includeNotAvailables: false,
                };
                return api.get(`${url}/params/transactionSubTypes`, data);
            },
            getInstrumentTypes: (transactionTypeId?: number | null) => {
                const params = transactionTypeId != null && transactionTypeId > 0 ? { transactionTypeId } : {};
                return api.get(`${url}/params/instrumentTypes`, params);
            },
            getInstrumentTypesForCustomInstrument: (transactionTypeId?: number | null) => {
                const params = transactionTypeId != null && transactionTypeId > 0 ? { transactionTypeId } : {};
                return api.get(`${url}/params/custominstrument/instrumentTypes`, params);
            },
            /** Solo Admin. Crea un tipo de instrumento. */
            insertInstrumentType: (data: { description: string; type: string; isAvailable?: boolean }) => {
                return api.post(`${url}/params/instrumentTypes`, data);
            },
            getMarkets: () => {
                return api.get(`${url}/params/markets`, {});
            },
            getAgents: () => {
                return api.get(`${url}/params/agents`, {});
            },
            getAgentTypes: () => {
                return api.get(`${url}/params/agentTypes`, {});
            },
            getAppVersionNumber: () => {
                // Obtiene el número de versión de la app
                let queryString = getAsQueryString({ name: appVersionNumber }, []);
                return api.get(`${url}/params/appVersionNumber?${queryString}`, null);
            },
            updateAppCurrentVersionNumber: (data: any) => {
                // Permite actualizar el número de versión de la app
                return api.post(`${url}/params/updateVersionNumber`, data);
            },
        },
        Portfolio: {
            get: () => {
                return api.get(`${url}/portfolio`, {});
            },
            insert: (data: any) => {
                return api.create(`${url}/portfolio/insert`, data);
            },
            hasWarnings: () => {
                return api.get(`${url}/portfolio/hasWarnings`, {});
            },
            update: (data: any) => {
                return api.create(`${url}/portfolio/update`, data);
            },
            delete: (data: any) => {
                return api.create(`${url}/portfolio/delete`, data);
            },
            totalValuePortfolio: (data: any) => {
                let promise = api.get(`${url}/totalValue/portfolio`, data);
                interceptors.Portfolio.ModifyOrderTotalValue(promise);
                return promise;
            },
            totalValueGroup: (data: any) => {
                let promise = api.get(`${url}/totalValue/portfolioGroup`, data);
                interceptors.Portfolio.ModifyOrderTotalValue(promise);
                return promise;
            },
            totalValueClient: (data: any) => {
                let promise = api.get(`${url}/totalValue/client`, data);
                interceptors.Portfolio.ModifyOrderTotalValue(promise);
                return promise;
            },
            evolucion: (data: any) => {
                //TODO: el fake backend no intercepta llamadas get, por eso se usa un create. cambiar por get
                return api.get(`${url}/totalValue/portfolio-percentage`, data);
            },
            composicionPortFolio: (filters: any) => {
                let listOfIntegerArrays = ['CurrencyIdList', 'PortfolioIdList'];
                let queryString = getAsQueryString(filters, listOfIntegerArrays);

                //La URL ya tendrá todos los filtros
                let _url = `${url}/totalValue/portfolio-percentage?${queryString}`;
                return api.get(_url, null);
            },
            composicion: (data: any) => {
                //TODO: el fake backend no intercepta llamadas get, por eso se usa un create. cambiar por get
                return api.create(`${url}/portfolio/composicion`, data);
            },
            indicadores: (data: any) => {
                //TODO: el fake backend no intercepta llamadas get, por eso se usa un create. cambiar por get
                return api.create(`${url}/portfolio/indicadores`, data);
            },
            portfolioDailyPerformance: (data: any) => {
                return api.get(`${url}/Dailyperformance/portfolio`, data);
            },
        },
        Stats: {},
        Report: {
            positions: (filters: any) => {
                let listOfIntegerArrays = [
                    'agentIdList',
                    'portfolioIdList',
                    'clientAccountIdList',
                    'instrumentsTypeIdList',
                    'instrumentIdList',
                ];
                let queryString = getAsQueryString(filters, listOfIntegerArrays);

                //La URL ya tendrá todos los filtros
                let _url = `${url}/report/positions?${queryString}`;
                return api.get(_url, null);
            },
        },
        RdConnection: {
            // Subset filters for impacting a selected price download.
            // mdEntryDate usually comes in YYYYMMDD format.
            // type and name are optional.
            // useSubset enables backend-side filtering.
            descargarInstrumentos: () => {
                return api.post(`${url}/rdconnection/download-instrument-data`, {});
            },
            descargarPrecios: () => {
                return api.post(`${url}/rdconnection/download-prices`, {});
            },
            descargarCashflow: () => {
                return api.post(`${url}/rdconnection/download-cashflows`, {});
            },
            impactarCashflow: (id: string) => {
                return api.post(`${url}/rdconnection/process-cashflows`, { id: id });
            },
            procesarUltimaInformacionDescargada: (
                id: any | null = null,
                fecha: Date | null = null,
                subsetFilters: { useSubset?: boolean; type?: string; mdEntryDate?: number | null; name?: string } | null = null
            ) => {
                let data: any = {
                    logLevel: 'Information',
                    showWarnings: true,
                    saveChanges: true,
                };
                if (id) {
                    data.impactarPreciosParaUnaFechaEspecifica = {
                        id: id,
                        date: fecha,
                        useSubset: subsetFilters?.useSubset === true,
                        subsetFilter: subsetFilters?.useSubset === true ? {
                            type: subsetFilters?.type || null,
                            mdEntryDate: subsetFilters?.mdEntryDate ?? null,
                            name: subsetFilters?.name || null
                        } : null
                    };
                }
                return api.post(`${url}/rdconnection/process-data`, data);
            },
            getPrimaryReferenceDownloadedInstrumentTypes: () => {
                return api
                    .get(`${url}/rdconnection/primary-reference-downloaded-instrument-types`, {})
                    .then((response) => {
                        if (response?.data?.length) {
                            response.data = response.data.map((instrumentType: any) => {
                                instrumentType.downloadedDateTime = moment(instrumentType.downloadedDateTime);
                                return instrumentType;
                            });
                        }
                        return response;
                    });
            },
            getPrimaryReferenceDownloadedInstrumentTypesDetail: (id: string) => {
                return api.get(`${url}/rdconnection/primary-reference-downloaded-instrument-types-detail`, { id: id });
            },
            getPrimaryReferenceDownloadedInstruments: () => {
                return api.get(`${url}/rdconnection/primary-reference-downloaded-instruments`, {});
            },
            getPrimaryReferenceDownloadedInstrumentsDetail: (id: string) => {
                return api.get(`${url}/rdconnection/primary-reference-downloaded-instrument-detail`, { id: id });
            },
            getPrimaryReferenceDownloadedPrices: () => {
                return api.get(`${url}/rdconnection/primary-reference-downloaded-prices`, {});
            },
            getPrimaryReferenceDownloadedPricesDetail: (
                id: string,
                filters?: { type?: string; mdEntryDate?: number | null; name?: string }
            ) => {
                const params: any = { id: id };
                if (filters?.type) params.type = filters.type;
                if (filters?.mdEntryDate != null) params.mdEntryDate = filters.mdEntryDate;
                if (filters?.name) params.name = filters.name;
                return api.get(`${url}/rdconnection/primary-reference-downloaded-prices-detail`, params);
            },
            getPrimaryReferenceDownloadedCashflows: () => {
                return api.get(`${url}/rdconnection/primary-reference-downloaded-cashflows`, {});
            },
            getPrimaryReferenceDownloadedCashflowsDetail: (id: string) => {
                return api.get(`${url}/rdconnection/primary-reference-downloaded-cashflows-detail`, { id: id });
            },
            getPrimaryReferenceDownloadedAgents: () => {
                return api.get(`${url}/rdconnection/primary-reference-downloaded-agents`, {}).then((response) => {
                    if (response?.data?.length) {
                        response.data = response.data.map((agent: any) => {
                            agent.downloadedDateTime = moment(agent.downloadedDateTime);
                            return agent;
                        });
                    }
                    return response;
                });
            },
            getPrimaryReferenceDownloadedAgentsDetail: (id: string) => {
                return api.get(`${url}/rdconnection/primary-reference-downloaded-agents-detail`, { id: id });
            },
            getAllEscoBolsaTransaccions: () => {
                return api.get(`${url}/rdconnection/get-all-escobolsa-transaccions`, {}).then((response) => {
                    if (response?.data?.length) {
                        response.data = response.data.map((transaccion: any) => {
                            transaccion.downloadedDateTime = moment(transaccion.downloadedDateTime);
                            return transaccion;
                        });
                    }
                    return response;
                });
            },
            getEscoBolsaTransaccionsDetail: (id: string) => {
                return api.get(`${url}/rdconnection/get-escobolsa-transaccions-detail`, { id: id });
            },
            saveEscoBolsaTransaccions: (data: any, type?: string) => {
                // Si se proporciona type, enviar como { type, data }, sino enviar data tal cual
                const payload = type ? { type, data } : data;
                return api.post(`${url}/rdconnection/save-escobolsa-transaccions`, payload);
            },
            updateEscoBolsaTransaccionImpact: (id: string, impactedDateTime: string, impactedSuccessfully: boolean) => {
                return api.post(`${url}/rdconnection/update-escobolsa-transaccion-impact`, {
                    id,
                    impactedDateTime,
                    impactedSuccessfully,
                });
            },
            deleteEscoBolsaTransaccion: (id: string) => {
                return api.delete(`${url}/rdconnection/delete-escobolsa-transaccion?id=${id}`);
            },
            proxyBolsaLogin: (bolsaApiUrl: string, userName: string, password: string) => {
                return api.post(`${url}/rdconnection/proxy-bolsa-login`, { bolsaApiUrl, userName, password });
            },
            proxyBolsaRequest: (
                bolsaApiUrl: string,
                endpoint: string,
                method: string = 'POST',
                headers?: Record<string, string>,
                body?: any,
                accessToken?: string
            ) => {
                return api.post(`${url}/rdconnection/proxy-bolsa-request`, {
                    bolsaApiUrl,
                    endpoint,
                    method,
                    headers,
                    body,
                    accessToken,
                });
            },
            getListReferenceDataForProcess: () => {
                return api.get(`${url}/rdconnection//get-last-reference-data-for-process`, {});
            },
            exportPrimaryReferenceCsvList: (selectedIds: any, backupAndDelete: boolean) => {
                let queryString =
                    'ids=' + selectedIds.join('&ids=') + '&deleteChildrenAfterDownload=' + backupAndDelete;
                return api.getFile(`${url}/rdconnection/export-primary-reference-csv-list?${queryString}`, undefined);
            },
        },
        Users: {
            get: () => {
                return api.get(`${url}/users`, {});
            },
            getByRole: (roleId: number) => {
                return api.get(`${url}/users/by-role`, { roleId });
            },
            getUser: () => {
                return api.get(`${url}/user`, {});
            },
            getUsers: (id: number) => {
                return api.get(`${url}/user/client`, { id: id });
            },
            update: (data: any) => {
                return api.post(`${url}/user/update`, data);
            },
            invite: (data: any) => {
                return api.post(`${url}/user/invite`, data);
            },
            remove: (id: any, auth0Id: any, state: boolean) => {
                return api.post(`${url}/user/delete`, {
                    id: id,
                    auth0Id: auth0Id,
                    isAvailable: state,
                });
            },
            drop: (id: any) => {
                return api.post(`${url}/user/drop`, id);
            },
            changePass: (data: any) => {
                return api.post(`${url}/user/change_password`, data);
            },
            invitation: (user: User, state: boolean) => {
                let data: any = {
                    firstname: user?.firstname,
                    lastname: user?.lastname,
                    clientId: user?.client?.id,
                    email: user?.email,
                    languageId: user?.language?.id,
                    auth0Id: user?.auth0Id,
                    roleId: user?.role?.id,
                    acceptNotifications: user?.acceptNotifications,
                };
                return state ? api.post(`${url}/user/resend`, data) : api.post(`${url}/user/reject`, data);
            },
        },
        Prices: {
            get: (filters: Cotizaciones_RequestConsulta) => {
                return api.get(`${url}/prices`, filters);
            },
            getDetail: (params: { instrumentId: number; currencyId: number; priceDate: string }) => {
                return api.get(`${url}/prices/detail`, params);
            },
            importCryptoUsdtArsClosingPrices: () => {
                return api.post(`${url}/prices/import-crypto-usdtars-closing-prices`, {});
            },
            insert: (data: any) => {
                return api.post(`${url}/prices/insert`, data);
            },
            delete: (id: any) => {
                return api.post(`${url}/prices/delete`, { id: id });
            },
            downloadXlsxTemplate: () => {
                return api.getFile(`${url}/prices/download-xlsx-template`, undefined);
            },
            importXlsx: (data: any) => {
                return api.createWithFile(`${url}/prices/import/xlsx`, data);
            },
            importCsv: (data: any) => {
                return api.createWithFile(`${url}/prices/import`, data);
            },
        },
        TotalValue: {
            recalculate: (date: any, clientId: any) => {
                return api.post(`${url}/TotalValue/recalculate`, { date: date, clientId: clientId });
            },
            cleanCompositionDuplicates: () => {
                return api.post(`${url}/TotalValue/clean-composition-duplicates`, {});
            },
            recalculateRange: (data: {
                startDate: Date;
                endDate: Date;
                clientId?: number | null;
                liquidarOperacionesAPlazo: boolean;
                liquidarPagosDeCuponAmortizacion: boolean;
                calcularTotalValue: boolean;
                usarRecalculoAlpha: boolean;
            }) => {
                return api.post(`${url}/TotalValue/detached-recalculate-range`, data);
            },
        },
        SignalR: {
            getActiveJobs: () => {
                return api.get(`${url}/signal-r/active-jobs`, {});
            },
            getActiveJobsByClient: () => {
                return api.get(`${url}/signal-r/active-jobs/by-client`, {});
            },
        },
        QATest: {
            generateStockMovementsFromHistory: (request: { clientId?: number | null; fromDate?: string; toDate?: string }) => {
                return api.post(`${url}/qa-test/generate-stock-movements-from-history`, request);
            },
            deleteDuplicateTransactions: (request: {
                clientId?: number | null;
                portfolioId?: number | null;
                agentCode?: string | null;
                transactionId?: string | null;
                fromDate?: string;
                toDate?: string;
            }) => {
                return api.post(`${url}/qa-test/duplicate-transactions/delete`, request);
            },
            deleteStockMovements: (request: {
                clientId?: number | null;
                portfolioId?: number | null;
                clientAccountId?: number | null;
                instrumentId?: number | null;
                fromDate?: string;
                toDate?: string;
                movementIds?: number[] | null;
                confirmDeleteAll: boolean;
            }) => {
                return api.post(`${url}/qa-test/stock-movements/delete`, request);
            },
            fixHoldingTransactionInstruments: () => {
                return api.post(`${url}/qa-test/fix-holding-transaction-instruments`, {});
            },
            backfillDeferredPaymentSaleRefMovement: (request: {
                clientId?: number | null;
                portfolioId?: number | null;
                dryRun: boolean;
            }) => {
                return api.post(`${url}/qa-test/deferred-payment-sales/backfill-ref-movement`, request);
            },
            liquidarOperacionesAPlazo: (date: any) => {
                return api.post(`${url}/ForwardOperations/procesar-vencimientos-op`, { date: date });
            },
            liquidarChequesYPagares: (date: any) => {
                return api.post(`${url}/qa-test/DeferredPaymentChecksAndPromissoryNotes`, { date: date });
            },
            liquidarPagosDeCuponAmortizacion: (date: any) => {
                return api.post(`${url}/qa-test/2027-impactar-pagos-para-fecha`, { dateToProcess: date });
            },
            recalculate2: (date: any, clientId: any) => {
                return api.post(`${url}/qa-test/recalculate2`, { date: date, clientId: clientId });
            },
        },
        Comercial: {
            get: () => {
                return api.get(`${url}/commercialAgent`, {});
            },
            insert: (data: any) => {
                return api.post(`${url}/commercialAgent/insert`, data);
            },
            update: (data: any) => {
                return api.post(`${url}/commercialAgent/update`, data);
            },
            remove: (data: any) => {
                return api.post(`${url}/commercialAgent/delete`, data);
            },
        },
        Auth0: {
            get: () => {
                return api.get(`${url}/user/auth0`, {});
            },
            firstlogin: () => {
                return api.post(`${url}/user/firstlogin`, {});
            },
        },
        Process: {
            get: () => {
                return api.get(`${url}/process/get`, {});
            },
        },
        ProcessExecutionStatus: {
            get: () => {
                return api.get(`${url}/processExecutionStatus/get`, {});
            },
        },
        ProcessExecution: {
            getLastN: (filter: ProcessExecution_GetRequest): any => {
                return api.get(`${url}/processExecution/getlastn`, filter);
            },
        },
    };

    //TODO dCestare: este código se debe a un extraño comportamiento en la API al pasar un array de enteros en un endpoint tipo GET. Ver esto: https://stackoverflow.com/a/11100414
    function getAsQueryString(filters: any, listOfIntegerArrays: string[]) {
        let listOfKeys = Object.keys(filters);
        //Obtengo todos los filtros que no son listas de integers y las agrego al query string
        let queryString = filters
            ? listOfKeys
                  .filter((key) => !listOfIntegerArrays.map((x) => x.toLowerCase()).includes(key.toLowerCase()))
                  .map((key) => key + '=' + filters[key])
                  .join('&')
            : '';

        //tratamiento especial para los filtros que son listas de integers
        listOfIntegerArrays.forEach((key) => {
            //Recorro los filtros que son arrays de enteros
            let arrayOfIntegers = filters[key];
            if (arrayOfIntegers?.length) {
                //si tienen un valor, los concateno de la siguiente forma: portfolioIdList=1&portfolioIdList=2&...
                queryString += '&' + arrayOfIntegers.map((_integer: number) => key + '=' + _integer).join('&');
            }
        });
        return queryString;
    }
}
